import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PBearingControlModule, PModuleMeasurement} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '../entityModule';
import {httpClient} from '@/api/baseApi';
import {getEventData, getEventTypes, getLastEvents} from './base';
import {isNumber} from '@/utils/commonUtils';

const moduleName = 'PbearingControlStoreModule';

function transformGet(elem: any): PBearingControlModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.BearingControlModule,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    taho: elem.taho,
    axe_3d: elem.axe_3d,
    gear_ratio: elem.gear_ratio,
    num_averaging: elem.num_averaging,
    is_hit_check: elem.is_hit_check,
    sensitivity: elem.sensitivity,
    low_taho_level: elem.low_taho_level,
    paddle_count: elem.paddle_count,
    is_balance_check: elem.is_balance_check,
    gost_low_level: elem.gost_low_level,
    gost_medium_level: elem.gost_medium_level,
    gost_high_level: elem.gost_high_level,
    modulation_low_level: elem.modulation_low_level,
    modulation_medium_level: elem.modulation_medium_level,
    modulation_high_level: elem.modulation_high_level,
    bearing_type: elem.bearing_type,
    measurements: [],
  };
}

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
    bottomWarningBoundary: elem.bottom_warning_boundary,
    bottomDangerBoundary: elem.bottom_danger_boundary,
    bottomErrorBoundary: elem.bottom_error_boundary,
  }));
}

function transformGetById(elem: any): PBearingControlModule {
  const model = transformGet(elem);
  model.measurements = getMeasurements(elem);
  return model;
}

function transformCreateEdit(elem: any): PBearingControlModule {
  const measurements: PModuleMeasurement[] = getMeasurements(elem);
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    type: PModuleType.BearingControlModule,
    description: elem.description,
    nodes: elem.nodes,
    alias: elem.alias,
    taho: elem.taho,
    axe_3d: elem.axe_3d,
    gear_ratio: elem.gear_ratio,
    num_averaging: elem.num_averaging,
    is_hit_check: elem.is_hit_check,
    sensitivity: elem.sensitivity,
    low_taho_level: elem.low_taho_level,
    paddle_count: elem.paddle_count,
    is_balance_check: elem.is_balance_check,
    gost_low_level: elem.gost_low_level,
    gost_medium_level: elem.gost_medium_level,
    gost_high_level: elem.gost_high_level,
    modulation_low_level: elem.modulation_low_level,
    modulation_medium_level: elem.modulation_medium_level,
    modulation_high_level: elem.modulation_high_level,
    bearing_type: elem.bearing_type,
    measurements: measurements,
  };
}

function transformSave(model: PBearingControlModule): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    if (isNumber(elem.bottomWarningBoundary))
      m.bottom_warning_boundary = elem.bottomWarningBoundary;
    if (isNumber(elem.bottomDangerBoundary)) m.bottom_danger_boundary = elem.bottomDangerBoundary;
    if (isNumber(elem.bottomErrorBoundary)) m.bottom_error_boundary = elem.bottomErrorBoundary;
    return m;
  });
  return {
    id: model.id,
    name: model.name,
    description: model.description,
    nodes: model.nodes,
    taho: model.taho,
    axe_3d: model.axe_3d,
    gear_ratio: model.gear_ratio,
    num_averaging: model.num_averaging,
    is_hit_check: model.is_hit_check,
    sensitivity: model.sensitivity,
    low_taho_level: model.low_taho_level,
    paddle_count: model.paddle_count,
    is_balance_check: model.is_balance_check,
    gost_low_level: model.gost_low_level,
    gost_medium_level: model.gost_medium_level,
    gost_high_level: model.gost_high_level,
    modulation_low_level: model.modulation_low_level,
    modulation_medium_level: model.modulation_medium_level,
    modulation_high_level: model.modulation_high_level,
    bearing_type: model.bearing_type,
    measurements: measurements,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class PBearingControlStoreModule extends EntityModule {
  protected routeName = 'bearing-control-modules';

  get all(): PBearingControlModule[] {
    return super.all as PBearingControlModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: PBearingControlModule}> {
    return super.create(transformCreateEdit) as Promise<{data: PBearingControlModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: PBearingControlModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: PBearingControlModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: PBearingControlModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: PBearingControlModule}> {
    return super.getById(id, transformGetById) as Promise<{data: PBearingControlModule}>;
  }

  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  //Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(PBearingControlStoreModule);
